import { X } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuCheckboxItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import type { QuoteStatus } from '../types';

// Common statuses to filter by (exclude rarely used ones from dropdown)
export const ALL_STATUSES: QuoteStatus[] = [
  'preparation',
  'sent',
  'waiting-po',
  'approved',
  'inprogress',
  'invoice-ready',
  'partially-invoiced',
  'invoiced',
  'cancelled',
  'rejected',
  'expired',
];

interface QuotesFiltersProps {
  selectedStatuses: QuoteStatus[];
  onStatusChange: (statuses: QuoteStatus[]) => void;
  pageSize: number;
  onPageSizeChange: (value: number) => void;
}

export function QuotesFilters({
  selectedStatuses,
  onStatusChange,
  pageSize,
  onPageSizeChange,
}: QuotesFiltersProps) {
  const { t } = useTranslation('quotes');

  const handleStatusToggle = (status: QuoteStatus) => {
    if (selectedStatuses.includes(status)) {
      onStatusChange(selectedStatuses.filter((s) => s !== status));
    } else {
      onStatusChange([...selectedStatuses, status]);
    }
  };

  const handleClearStatuses = () => {
    onStatusChange([]);
  };

  const statusButtonLabel =
    selectedStatuses.length === 0
      ? t('filters.allStatuses')
      : selectedStatuses.length === ALL_STATUSES.length
        ? t('filters.allStatuses')
        : t('filters.statusCount', { count: selectedStatuses.length });

  return (
    <div className="flex flex-wrap items-center gap-3">
      {/* Status Filter (Multi-select) */}
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="outline" className="w-40 justify-between">
            {statusButtonLabel}
            {selectedStatuses.length > 0 && selectedStatuses.length < ALL_STATUSES.length && (
              <X
                className="h-3 w-3 ml-1 hover:text-destructive"
                onClick={(e) => {
                  e.stopPropagation();
                  handleClearStatuses();
                }}
              />
            )}
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start" className="w-48">
          {ALL_STATUSES.map((status) => (
            <DropdownMenuCheckboxItem
              key={status}
              checked={selectedStatuses.includes(status)}
              onCheckedChange={() => handleStatusToggle(status)}
            >
              {t(`status.${status}`)}
            </DropdownMenuCheckboxItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>

      {/* Page Size */}
      <Select value={String(pageSize)} onValueChange={(v) => onPageSizeChange(Number(v))}>
        <SelectTrigger className="w-24">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="10">10</SelectItem>
          <SelectItem value="25">25</SelectItem>
          <SelectItem value="50">50</SelectItem>
          <SelectItem value="100">100</SelectItem>
        </SelectContent>
      </Select>
    </div>
  );
}
